import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { hasSession, setStoredUser } from '@/services/authToken'
import {
  fetchMe,
  fetchProfile,
  fetchTimezones,
  readSession,
  requestPasswordReset,
  signIn,
  signOut,
  updateProfile,
} from './api'

/**
 * The signed-in session.
 *
 * This used to be a query with no network call, because the API had no endpoint
 * to ask. `GET /auth/me` is that endpoint, so it is a real fetch now — which
 * means a renamed user, a changed avatar or a new permission map reaches an open
 * tab on its next mount rather than persisting until sign-out.
 *
 * `initialData` is the profile sign-in persisted, so the top bar renders a name
 * on the first frame after a reload instead of a dash that fills in a moment
 * later. The fetch then confirms or corrects it, and writes the result back to
 * storage so the next reload starts from the newer copy.
 *
 * `enabled` on hasSession() because signed out there is nobody to ask about, and
 * an unauthenticated `/auth/me` would 401 — which the response interceptor would
 * correctly read as an expired session and act on.
 *
 * Not retried: a failing `/auth/me` is either a dead session, which the
 * interceptor already handles, or a server fault that a second identical request
 * will not talk its way out of.
 */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async ({ signal }) => {
      const profile = await fetchMe({ signal })
      setStoredUser(profile)
      return profile
    },
    initialData: readSession,
    /* Dated to the epoch, so the stored copy paints the first frame but is
       already stale. Without it React Query stamps `initialData` as fetched
       now, the 30s `staleTime` holds, and the fetch described above never
       runs on load — leaving the logo and avatar URLs, which are stripped
       before storage, null until something else refetches. */
    initialDataUpdatedAt: 0,
    enabled: hasSession(),
    retry: false,
  })
}

export function useSignIn() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: signIn,
    // Never retried. A failed sign-in is a rejected credential, and retrying it
    // burns an attempt against any server-side lockout counter for no reason.
    retry: false,
    onSuccess: (session) => {
      // Everything `/auth/me` returned, not just the person — the account name,
      // the roster shifts and the permission map are read off this same key.
      client.setQueryData(queryKeys.session, {
        user: session.user,
        account: session.account,
        shifts: session.shifts,
        permissions: session.permissions,
      })
    },
  })
}

/**
 * Save the signed-in user's own profile.
 *
 * Writes the returned profile straight into the session key rather than
 * invalidating it. The mutation has just been told what the record now is, so a
 * refetch would ask a question that was answered a millisecond ago — and would
 * leave the top bar showing the old name for the length of that round trip.
 *
 * Not retried. A rejected password or a taken email address is an answer, and
 * sending it twice gets the same answer while spending an attempt against
 * whatever the server counts.
 */
export function useUpdateProfile() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: updateProfile,
    retry: false,
    onSuccess: ({ record, profile }) => {
      // The session drives the shell; the record drives the form. Both were
      // just answered for, so both are written rather than invalidated.
      client.setQueryData(queryKeys.session, profile)
      client.setQueryData(queryKeys.profile, record)
    },
  })
}

/**
 * The editable profile record.
 *
 * Separate from `useSession` because it is a separate endpoint answering a
 * separate question — `/users/me` is the row, `/auth/me` is the session. Only
 * the profile form asks for it, so it is fetched when that form opens rather
 * than on every load.
 *
 * Not cached across opens: `avatar_view_url` on it is a presigned address that
 * expires in five minutes, so a record held longer than that renders a broken
 * image. Re-read on open is both simpler and correct.
 */
/**
 * The zone list the API accepts.
 *
 * Reference data: it changes when the server's tzdata does, which is to say
 * almost never, so it is cached for the session rather than re-fetched each time
 * the form opens. Nothing here expires, unlike the profile record beside it.
 */
export function useTimezones({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.timezones,
    queryFn: ({ signal }) => fetchTimezones({ signal }),
    enabled: enabled && hasSession(),
    staleTime: Infinity,
    retry: false,
  })
}

export function useProfile({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: ({ signal }) => fetchProfile({ signal }),
    enabled: enabled && hasSession(),
    gcTime: 0,
    retry: false,
  })
}

/**
 * Ask for a reset link.
 *
 * No cache to touch: nobody is signed in, and the response carries nothing about
 * any account — see `requestPasswordReset`.
 *
 * Not retried. The request either reached the server, in which case asking again
 * mails a second link and invalidates nothing, or it did not, in which case the
 * person can press the button themselves. An automatic retry here is how
 * somebody gets three reset emails for one click.
 */
export function useRequestPasswordReset() {
  return useMutation({ mutationFn: requestPasswordReset, retry: false })
}

export function useSignOut() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: signOut,
    // Everything cached was scoped to the session that just ended. Cleared on
    // settle rather than success because signOut cannot fail — and if it ever
    // could, keeping another tenant's data in the cache would be the worse bug.
    onSettled: () => client.clear(),
  })
}
